import { faCircleCheck, faCircleXmark, faLink, faLinkSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Autocomplete, Button, IconButton, TableCell, TextField, Typography, useTheme } from "@mui/material";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { SinglePlaceholderProps as Props } from "./interfaces";
import { StyledTableRow } from "./styles";
import { DocumentTemplatePlaceholderRef } from "../../../../models/document-templates/document-template-placeholder";
import {
  DOCUMENT_TEMPLATE_PLACEHOLDER_FIELD_TYPES,
  getFieldType,
  getSubField,
  isLinkablePlaceholder,
} from "../../../../utils/constants/document-template-placeholder-field-types";

const DocumentTemplateSinglePlaceholderConfig = ({ placeholder, handleUpdatePlaceholder, allPlaceholders }: Props) => {
  const tRP = "components.document_templates.configure_placeholders";
  const { t } = useTranslation();
  const [linkingToPlaceholder, setIsLinkingToPlaceholder] = useState(false);
  const [linkedToPlaceholder, setLinkedToPlaceholder] = useState<DocumentTemplatePlaceholderRef | null>(null);
  const theme = useTheme();

  const canLinkToPlaceholders: DocumentTemplatePlaceholderRef[] = allPlaceholders.filter(
    (_placeholder) =>
      _placeholder.id !== placeholder.id && _placeholder.fieldType && isLinkablePlaceholder(_placeholder.fieldType)
  );

  const relatedFieldType = getFieldType(placeholder.relatedToPlaceholder?.fieldType ?? "");

  const updatePlaceholder = useCallback(() => {
    setIsLinkingToPlaceholder(false);
    handleUpdatePlaceholder({
      ...placeholder,
      relatedToPlaceholder: linkedToPlaceholder,
    });
  }, [linkedToPlaceholder, handleUpdatePlaceholder, placeholder]);

  return (
    <>
      <StyledTableRow>
        <TableCell>{placeholder.name}</TableCell>
        {linkingToPlaceholder ? (
          <TableCell style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Autocomplete
              options={canLinkToPlaceholders}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={`${t(`${tRP}.select_placeholder`)}`}
                />
              )}
              onChange={(_, value) => setLinkedToPlaceholder(value)}
              getOptionLabel={(option) => option.name}
              aria-placeholder={`${t(`${tRP}.select_placeholder`)}`}
            />
            <IconButton
              sx={{
                boxShadow: 2,
              }}
              onClick={updatePlaceholder}
              style={{
                color: theme.palette.success.main,
              }}
            >
              <FontAwesomeIcon icon={faCircleCheck} />
            </IconButton>
            <IconButton
              sx={{
                boxShadow: 2,
              }}
              style={{
                color: theme.palette.error.main,
              }}
              onClick={() => {
                setIsLinkingToPlaceholder(false);
                setLinkedToPlaceholder(null);
              }}
            >
              <FontAwesomeIcon icon={faCircleXmark} />
            </IconButton>
          </TableCell>
        ) : (
          <>
            <TableCell>
              {!placeholder.relatedToPlaceholder ? (
                <Autocomplete
                  value={Object.values(DOCUMENT_TEMPLATE_PLACEHOLDER_FIELD_TYPES).find(
                    (fieldType) => fieldType.id === placeholder.fieldType
                  )}
                  options={Object.values(DOCUMENT_TEMPLATE_PLACEHOLDER_FIELD_TYPES)}
                  sx={{ width: 300 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={`${t(`${tRP}.select_field`)}`}
                    />
                  )}
                  getOptionLabel={(option) => option.name}
                  onChange={(_, value) =>
                    handleUpdatePlaceholder({
                      ...placeholder,
                      fieldType: value?.id ?? null,
                    })
                  }
                />
              ) : (
                <>
                  <Autocomplete
                    value={getSubField(
                      placeholder.relatedToPlaceholder.fieldType ?? "",
                      placeholder.subfieldType ?? ""
                    )}
                    options={relatedFieldType?.subFields ?? []}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={`${t(`${tRP}.select_field`)}`}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <Typography
                              color={theme.palette.text.secondary}
                            >{`${placeholder.relatedToPlaceholder?.name} (${relatedFieldType?.name})`}</Typography>
                          ),
                        }}
                      />
                    )}
                    onChange={(_, value) =>
                      handleUpdatePlaceholder({
                        ...placeholder,
                        subfieldType: value?.name ?? "",
                      })
                    }
                    getOptionLabel={(option) => option.label}
                  />
                </>
              )}
            </TableCell>
            <TableCell align="center">
              {!placeholder.relatedToPlaceholder && canLinkToPlaceholders.length > 0 ? (
                <Button
                  sx={{
                    boxShadow: 3,
                  }}
                  variant="outlined"
                  color={"primary"}
                  onClick={() => setIsLinkingToPlaceholder(true)}
                >
                  <FontAwesomeIcon icon={faLink} />
                  {`${t(`${tRP}.link_to_placeholder`)}`}
                </Button>
              ) : placeholder.relatedToPlaceholder ? (
                <Button
                  sx={{
                    boxShadow: 2,
                  }}
                  variant="outlined"
                  color={"error"}
                  onClick={() => {
                    setIsLinkingToPlaceholder(false);
                    handleUpdatePlaceholder({
                      ...placeholder,
                      relatedToPlaceholder: null,
                    });
                  }}
                >
                  <FontAwesomeIcon icon={faLinkSlash} />
                  {`${t(`${tRP}.unlink_from_placeholder`)}`}
                </Button>
              ) : (
                <></>
              )}
            </TableCell>
          </>
        )}
      </StyledTableRow>
    </>
  );
};

export default DocumentTemplateSinglePlaceholderConfig;
